// Shared emulator-core registry for the web tier.
//
// The main player auto-detects the core by ROM filename extension
// (.nes / .gb), with a `?core=` URL param as an explicit override
// for testing and TAS replay. Adding a new core means installing
// the core package and appending one entry here.
//
// Each entry pairs a stable ID (the `?core=` URL param value) with
// a factory that constructs an EmulatorCore from runtime options.
// Both the contract types and the per-core factories come from
// external packages — see packages/core-contract and
// packages/cores/<id>/.

import {
  supportsAudioSynth,
  type EmulatorCore, type EmulatorCoreOptions,
  type BaseCore, type AudioSynthCapable, type ChipId,
} from "@multinostalgia/core-contract";
import * as nesjs from "@multinostalgia/core-nes-nesjs/web";
import * as gbRoblouie from "@multinostalgia/core-gb-roblouie/web";

export interface CoreEntry {
  id: string;
  // Friendly system name, used as the folder label in the ROM picker
  // when grouping saved games by console.
  label: string;
  // ROM filename extensions this core handles (lowercase, with the
  // dot). Drives both launch-time core inference and the ROM picker's
  // `accept` filter — one place to touch when a core gains a format.
  extensions: string[];
  create(opts: EmulatorCoreOptions): EmulatorCore;
}

export const CORES: CoreEntry[] = [
  { id: nesjs.id,      label: "NES",      extensions: [".nes"], create: nesjs.create },
  { id: gbRoblouie.id, label: "Game Boy", extensions: [".gb"],  create: gbRoblouie.create },
];

export const DEFAULT_CORE_ID = nesjs.id;

// Pick the core ID for this session:
//   1. `?core=<id>` URL param, if present and known. This is the
//      developer / TAS-playback escape hatch — a TAS recording made
//      under a specific core must replay under it.
//   2. DEFAULT_CORE_ID otherwise.
//
// In the common path, the resolved value here only matters as a
// fallback: the launch path overrides it from the ROM filename
// extension (.gb / .nes).
export function resolveCoreId(): string {
  try {
    const url = new URLSearchParams(window.location.search).get("core");
    if (url && CORES.some(c => c.id === url)) return url;
  } catch { /* ignore */ }
  return DEFAULT_CORE_ID;
}

// Always returns an entry — falls back to CORES[0] for an unknown
// id rather than throwing, so a typo or stale value can't crash boot.
export function findCore(id: string): CoreEntry {
  return CORES.find(c => c.id === id) ?? CORES[0]!;
}

// Pick the core id for a ROM by filename extension; null when no
// registered core claims the extension (the caller falls back to the
// session's resolved core id).
export function inferCoreIdFromName(name: string): string | null {
  const lower = name.toLowerCase();
  for (const c of CORES) {
    if (c.extensions.some(ext => lower.endsWith(ext))) return c.id;
  }
  return null;
}

// Friendly console name for a ROM by filename extension — the folder
// the picker files it under. Falls back to "Other" when no registered
// core claims the extension (so an unknown ROM still has a home).
export function coreLabelForName(name: string): string {
  const id = inferCoreIdFromName(name);
  return CORES.find(c => c.id === id)?.label ?? "Other";
}

// The ROM picker's `accept` attribute value: every extension any
// registered core handles.
export function romPickerAccept(): string {
  return CORES.flatMap(c => c.extensions).join(",");
}

// Which registered core synthesizes a given sound chip (docs/sound.md
// §3): the chip's host system maps to exactly one core today.
const CHIP_CORE_ID: Record<ChipId, string> = {
  "nes-2a03": nesjs.id,
  "gb-dmg": gbRoblouie.id,
};

// Build a dedicated synth core for a sound chip — a fresh core of the
// chip's system with sound on. Returned to MunosEmulatorHost as its
// makeSynthCore factory so play_sound can voice a VGM. Null when no
// registered core covers the chip (the script's sound stays silent).
export function makeSynthCore(chip: ChipId, sampleRate: number): (BaseCore & AudioSynthCapable) | null {
  const coreId = CHIP_CORE_ID[chip];
  if (!coreId) return null;
  const core = findCore(coreId).create({ emulateSound: true, sampleRate });
  return supportsAudioSynth(core) ? core : null;
}
